import { AnalyticsLite } from "../AnalyticsLite";
import type { PluginConfig } from "../types";
import { parseCookie, parseCookies } from "./utils";
import type { ConsentMap } from "./types";
import { AnalyticsModuleBootstrapError, logger } from "../../debug";

declare global {
  interface Document {
    addEventListener(
      type: "cookiechange",
      listener: (
        this: any,
        ev: CustomEvent<{ oldValue: string; newValue: string }>
      ) => void
    ): void;
    addEventListener(
      type: "consent-change",
      listener: (this: any, ev: CustomEvent<ConsentMap>) => void
    ): void;
  }
}

let consentMap: ConsentMap = {};
let cookieMap = {};

export function parseConsentMap(consentString: string) {
  if (typeof consentString !== "string") {
    logger.warn("Consent string is not a string, returning empty consent map");
    return {};
  }
  return (consentString ?? "").split(",").reduce((consentMap, consentGroup) => {
    const [group, value] = consentGroup.split(":");
    consentMap[group] = value;
    return consentMap;
  }, {});
}

function whenPluginRegistered(pluginName: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const analyticsInstance = self?.analytics;
    if (!analyticsInstance?.analytics) {
      return reject("Analytics library not found");
    }
    if (analyticsInstance.plugins[pluginName]) {
      return resolve();
    }
    analyticsInstance.analytics.once(`registerPlugin:${pluginName}`, () => {
      resolve();
    });
  });
}

function parseConsentCookie(consentCookie: string): ConsentMap {
  try {
    // Receiving the consent group string, note that the INTL cookie doesn't have a groups key
    const rawCookieConsentData =
      cookieMap[consentCookie]?.groups ?? cookieMap?.[consentCookie];

    return parseConsentMap(rawCookieConsentData);
  } catch (error) {
    logger.error(
      new AnalyticsModuleBootstrapError("Failed to parse consent cookie", {
        cause: error,
      })
    );
    return {};
  }
}

export function getCookieMap() {
  return { ...cookieMap };
}

export function getConsentMap(): ConsentMap {
  return { ...consentMap };
}

export function setupConsentUpdateDispatch() {
  if (typeof window === "undefined") {
    return;
  }
  const CONSENT_UPDATE_EVENT = "consentUpdate";
  document.addEventListener("consent-change", (ev) => {
    const nextConsentMap = ev.detail.consentMap ?? {};
    const analyticsDispatch = window?.analytics?.analytics?.dispatch;
    if (typeof analyticsDispatch === "function") {
      analyticsDispatch({
        type: CONSENT_UPDATE_EVENT,
        ...nextConsentMap,
      });
    }
  });
  // Initial dispatch to set the consent state on analytics library
  const analyticsDispatch = window?.analytics?.analytics?.dispatch;
  analyticsDispatch?.({
    type: CONSENT_UPDATE_EVENT,
    ...consentMap,
  });
}

// In the future we should make the consent mechanism configurable
export function startCookieMonitoring(
  plugins: PluginConfig,
  consentCookie: string
): void {
  if (typeof window === "undefined") {
    logger.debug("Not within document context");
    return;
  }

  if (consentCookie === undefined || consentCookie === "") {
    logger.debug("No consent cookie defined, skipping consent");
    return;
  }

  cookieMap = parseCookies();
  consentMap = parseConsentCookie(consentCookie);

  const setConsent = (function () {
    let timer: ReturnType<typeof setTimeout>;
    return (consentGroups: string) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        consentMap = { ...consentMap, ...parseConsentMap(consentGroups) };
        document.dispatchEvent(
          new CustomEvent("consent-change", {
            detail: {
              consentMap,
            },
          })
        );
      }, 100);
    };
  })();

  document.addEventListener("cookiechange", (e) => {
    cookieMap = parseCookie(e.detail.newValue);
    if (consentCookie === undefined || consentCookie === "") {
      return;
    }
    if (cookieMap[consentCookie]) {
      // Getting cookie params in object key notation
      const rawCookieConsentData =
        cookieMap[consentCookie]?.groups ?? cookieMap?.[consentCookie];
      setConsent(rawCookieConsentData);
    }
  });

  document.addEventListener("consent-change", (ev) => {
    Object.keys(plugins).forEach((plugin) => {
      if (plugins[plugin]["consentLevel"]) {
        let consentLevel = plugins[plugin]["consentLevel"];
        if (typeof consentLevel === "function") {
          consentLevel = consentLevel(getConsentMap());
        }

        if (consentLevel === false) {
          // Disabled doesn't care, it's pretty funny actually
          const pluginName = (plugins?.[plugin]?.["name"] as string) ?? plugin;
          logger.debug(`disabling plugin ${pluginName}`, plugins?.[plugin]);
          // Simulated plugins are also disabled via the same event
          window["analytics"]["analytics"].disablePlugin(pluginName);
          return;
        }

        // TODO: Determine whether this should be above 122
        if (plugins[plugin].ignoreConsentUpdates === true) {
          return;
        }

        // TODO: While testing found a new limtation of the analytics library, however, the limitation comes from
        // a safe assumption in the library where the plugin would exist already. So while we could go down
        // the route of a try-catch I don't want to disrupt events so we'll check ahead instead
        // The window plugin key names have mismatches to what the function is receiving so we rely on the below util function to have a match
        if (consentLevel === true) {
          if (AnalyticsLite.emulatedPlugins.has(plugin)) {
            logger.debug(`enabling simulated plugin ${plugin}`);
            window["analytics"]["analytics"].dispatch({
              type: "simulatedPluginEnabled",
              plugins: [plugin],
            });
          } else {
            logger.debug(`enabling plugin ${plugin}`, plugins?.[plugin]);
            whenPluginRegistered(plugin)
              .then(() => {
                window["analytics"]["analytics"].enablePlugin(plugin);
              })
              .catch((err) => {
                logger.error(`Error enabling plugin ${plugin}: ${err}`);
              });
          }
        }
      }
    });
  });
}
